"use client";

import React, { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
const neurosurgery = [{
  name: "Initial consultation with a neurosurgeon",
  price: "€180"
}, {
  name: "Primary remote consultation",
  price: "€180"
}, {
  name: "Follow-up visit after surgery",
  price: "€0"
}, {
  name: "Microdiscectomy\xA0",
  price: "from €6,000"
}, {
  name: "Nucleoplasty\xA0",
  price: "from €4,320"
}, {
  name: "Vertebroplasty\xA0",
  price: "from €3,960"
}, {
  name: "Peripheral nerve surgery\xA0",
  price: "from €1,440"
}];
const painManagement = [{
  name: "Initial doctor's consultation",
  price: "€150"
}, {
  name: "Repeated doctor's consultation",
  price: "€90"
}, {
  name: "Follow-up visit after treatment",
  price: "€50"
}, {
  name: "Epidural steroid injection",
  price: "€420"
}, {
  name: "Ultrasound-guided invasive procedure",
  price: "€360"
}, {
  name: "X-ray guided invasive procedure",
  price: "€660"
}, {
  name: "Invasive procedure under X-ray control with anaesthesia",
  price: "€960"
}, {
  name: "Cryodenervation",
  price: "€865"
}, {
  name: "Cryodenervation with anaesthesia",
  price: "€1,150"
}, {
  name: "PRP (platelet-rich plasma) therapy",
  price: "€865"
}, {
  name: "PRP therapy with anaesthesia",
  price: "€1,200"
}, {
  name: "Steroid injection into the joint",
  price: "from €150"
}, {
  name: "Hyaluronic injection",
  price: "€220"
}, {
  name: "Collagen peptide injection",
  price: "€145-240"
}, {
  name: "Prolotherapy",
  price: "€115"
}, {
  name: "Ultrasound-guided prolotherapy",
  price: "€170"
}, {
  name: "Intravenous medicine injection",
  price: "€40-60"
}];
const additionalCosts = [{
  item: "Flights Dublin to Kaunas",
  price: "Variable"
}, {
  item: "Hotel accommodation (pre-surgery)",
  price: "Variable"
}, {
  item: "Meals before surgery",
  price: "Variable"
}, {
  item: "Accommodation at Egle Rehabilitation center (private room, 5 meals/day)",
  price: "€190/day"
}];
const packageA = ["Remote consultation with your neurosurgeon (MRI review)", "Hotel reservation before arrival", "All medical transfers in Lithuania", "Personal coordinator available by phone throughout your stay"];
const packageB = ["Hotel reservation before arrival", "All medical transfers during your consultation visit", "Personal coordinator available by phone during your visit"];
const reimbursements = [{
  procedure: "Microdiscectomy",
  hse: "Up to €8,558"
}, {
  procedure: "Nucleoplasty",
  hse: "Up to €8,558"
}, {
  procedure: "Vertebroplasty",
  hse: "Up to €8,558"
}, {
  procedure: "Pain management procedures",
  hse: "Up to €874"
}, {
  procedure: "Outpatient consultation",
  hse: "Up to €194"
}];
function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative w-full bg-[#264256] overflow-hidden min-h-[40vh] md:min-h-[50vh] flex items-center">
      <div className="container mx-auto px-6 max-w-7xl relative z-10 py-10 md:py-16">
        <div ref={ref} className={cn("flex flex-col items-center text-center gap-4 md:gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">Transparent Pricing</span>
          <h1 className="font-default font-bold text-4xl md:text-[60px] leading-[1.1] text-white max-w-4xl">Costs confirmed before you travel.</h1>
          <p className="font-default text-white/80 text-base md:text-xl leading-relaxed max-w-2xl">All prices are fixed. No hidden fees. HSE reimbursement can cover up to €8,558 of your surgical costs under the EU Cross-Border Directive.</p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
              <Link to="/#request-your-consultation">Book a Consultation</Link>
            </Button>
            <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
              <a href="/how-it-works/">HSE Reimbursement Info</a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
function PricesSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [showAllPain, setShowAllPain] = useState(false);
  const visiblePain = showAllPain ? painManagement : painManagement.slice(0, 6);
  return <section className="bg-background py-12 md:py-20">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] mb-3">Our Prices</h2>
            <p className="font-default text-muted-foreground text-base max-w-2xl mx-auto">All prices include VAT. Surgical prices include surgery, treatment and inpatient care. Prices are confirmed in writing before you travel.</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="w-full lg:w-[50%]">
              <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden h-full">
                <div className="bg-[#264256] px-6 py-4">
                  <h3 className="font-default font-bold text-white text-base uppercase tracking-wider">Spine Surgery</h3>
                </div>
                <div className="divide-y divide-[#f1f3f5]">
                  {neurosurgery.map((item, i) => <div key={i} className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 gap-4">
                      <span className="font-default text-[#264256] text-sm leading-snug">{item.name}</span>
                      <span className="font-default font-bold text-[#264256] text-sm md:text-base shrink-0">{item.price}</span>
                    </div>)}
                </div>
              </div>
            </div>
            <div className="w-full lg:w-[50%]">
              <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
                <div className="bg-[#264256] px-6 py-4">
                  <h3 className="font-default font-bold text-white text-base uppercase tracking-wider">Pain Management</h3>
                </div>
                <div className="divide-y divide-[#f1f3f5]">
                  {visiblePain.map((item, i) => <div key={i} className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 gap-4">
                      <span className="font-default text-[#264256] text-sm leading-snug">{item.name}</span>
                      <span className="font-default font-bold text-[#264256] text-sm md:text-base shrink-0">{item.price}</span>
                    </div>)}
                </div>
                {!showAllPain && <button onClick={() => setShowAllPain(true)} className="w-full flex items-center justify-center gap-2 px-6 py-4 text-[#264256] font-default text-sm font-bold uppercase tracking-wider hover:bg-[#eefaf4] transition-colors border-t border-[#f1f3f5]">
                    Show all procedures
                    <ChevronDown className="w-4 h-4" />
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function CoordinationPackageSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background pb-12 md:pb-20">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] mb-3">Patient Coordination and Care</h2>
            <p className="font-default text-muted-foreground text-base max-w-2xl mx-auto">A personal coordinator handles your hotel reservation, pre-operative tests, all medical appointments and transfers in Lithuania. One phone number for everything from arrival to departure.</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="w-full lg:w-[50%] flex flex-col">
              <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden flex flex-col h-full">
                <div className="bg-[#264256] px-6 py-4 flex items-center justify-between">
                  <h3 className="font-default font-bold text-white text-base uppercase tracking-wider">Package A</h3>
                  <span className="font-default font-bold text-[#65d19d] text-xl">€600</span>
                </div>
                <div className="px-6 py-3 bg-[#eefaf4] border-b border-[#a9e5c9]">
                  <p className="font-default text-[#264256] text-xs font-semibold uppercase tracking-wider">REMOTE CONSULTATION + SURGERY + REHABILITATION CENTRE</p>
                </div>
                <div className="flex flex-col gap-0 divide-y divide-[#f1f3f5] flex-1">
                  {packageA.map((item, i) => <div key={i} className="flex items-start gap-3 px-6 py-3 md:py-4">
                      <div className="bg-[#eefaf4] rounded-full p-1 shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#65d19d]" strokeWidth={3} />
                      </div>
                      <span className="font-default text-[#264256] text-sm leading-snug">{item}</span>
                    </div>)}
                </div>
                <div className="px-6 py-4 border-t border-[#f1f3f5]">
                  <p className="font-default text-muted-foreground text-xs leading-relaxed">Egle Rehabilitation center rate: €190 per day including private room and 5 meals. Recommended stay: 12 to 20 days. Total: €2,280 to €3,800.</p>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-[50%] flex flex-col">
              <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden flex flex-col h-full">
                <div className="bg-[#264256] px-6 py-4 flex items-center justify-between">
                  <h3 className="font-default font-bold text-white text-base uppercase tracking-wider">Package B</h3>
                  <span className="font-default font-bold text-[#65d19d] text-xl">€400</span>
                </div>
                <div className="px-6 py-3 bg-[#eefaf4] border-b border-[#a9e5c9]">
                  <p className="font-default text-[#264256] text-xs font-semibold uppercase tracking-wider">In-person consultation visit only</p>
                </div>
                <div className="flex flex-col gap-0 divide-y divide-[#f1f3f5] flex-1">
                  {packageB.map((item, i) => <div key={i} className="flex items-start gap-3 px-6 py-3 md:py-4">
                      <div className="bg-[#eefaf4] rounded-full p-1 shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#65d19d]" strokeWidth={3} />
                      </div>
                      <span className="font-default text-[#264256] text-sm leading-snug">{item}</span>
                    </div>)}
                </div>
                <div className="px-6 py-4 border-t border-[#f1f3f5]">
                  <p className="font-default text-muted-foreground text-xs leading-relaxed">All transfers operate within the Kaunas region and include: Kaunas Airport, hotel and Clinic Agatas. Transfers outside this area and personal transfers are not included.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function AdditionalCostsSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-[#eefaf4] border-y border-[#a9e5c9] py-12 md:py-20">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="hidden lg:grid lg:grid-cols-[55fr_45fr] lg:grid-rows-[auto_auto_1fr_auto] gap-x-16 gap-y-5">
            <h2 className="font-default font-bold text-4xl text-[#264256] leading-tight">Additional costs</h2>
            <h2 className="font-default font-bold text-4xl text-[#264256] leading-tight">HSE reimbursement rates</h2>
            <p className="font-default text-muted-foreground text-base leading-relaxed">Flights, hotel and meals before surgery are your responsibility. Egle Rehabilitation center accommodation and meals are billed directly by the center at €190 per day.</p>
            <p className="font-default text-muted-foreground text-base leading-relaxed">HSE reimburses the lesser of what you paid or the Irish public rate for the same procedure. Rates below are from the official HSE day case price list effective 1 July 2025.</p>
            <div className="bg-white rounded-lg border border-[#a9e5c9] overflow-hidden shadow-sm flex flex-col">
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-sm uppercase tracking-wider">What you pay separately</h3>
              </div>
              <div className="divide-y divide-[#eefaf4] flex-1">
                {additionalCosts.map((item, i) => <div key={i} className="flex items-center justify-between px-6 py-4 gap-4">
                    <span className="font-default text-[#264256] text-sm leading-snug">{item.item}</span>
                    <span className="font-default font-bold text-[#264256] text-sm shrink-0">{item.price}</span>
                  </div>)}
              </div>
              <div className="px-6 py-4 border-t border-[#eefaf4] mt-auto">
                <p className="font-default text-muted-foreground text-xs leading-relaxed">Egle Rehabilitation center rate: €190 per day including private room and 5 meals. Recommended stay: 12 to 20 days (following 1 to 2 nights post-operative care at Clinic Agatas). Total: €2,280 to €3,800. Partial HSE reimbursement may apply for physiotherapy procedures.</p>
                <a href="/how-it-works/" className="font-default text-[#264256] text-xs font-bold uppercase tracking-wider hover:text-[#65d19d] transition-colors mt-2 block">HSE reimbursement explained</a>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#a9e5c9] overflow-hidden shadow-sm flex flex-col">
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-sm uppercase tracking-wider">Maximum HSE Reimbursement</h3>
              </div>
              <div className="divide-y divide-[#eefaf4] flex-1">
                {reimbursements.map((item, i) => <div key={i} className="flex items-center justify-between px-6 py-4 gap-4">
                    <span className="font-default text-[#264256] text-sm">{item.procedure}</span>
                    <span className="font-default font-bold text-[#264256] text-base shrink-0">{item.hse}</span>
                  </div>)}
              </div>
              <div className="px-6 py-4 border-t border-[#eefaf4] mt-auto">
                <div className="bg-[#264256] rounded-lg p-4">
                  <p className="font-default text-white/80 text-xs leading-relaxed">All procedures at Clinic Agatas are classified as day case under Lithuanian healthcare regulations. HSE reimbursement is calculated at the Irish day case rate.</p>
                  <a href="/how-it-works/" className="font-default text-[#65d19d] text-xs font-bold uppercase tracking-wider hover:underline mt-2 block">HSE reimbursement explained</a>
                </div>
              </div>
            </div>
            <div className="col-span-2"></div>
          </div>

          <div className="flex flex-col gap-8 lg:hidden">
            <div className="flex flex-col gap-4">
              <h2 className="font-default font-bold text-2xl text-[#264256] leading-tight">Additional costs</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">Flights, hotel and meals before surgery are your responsibility. Egle Rehabilitation center accommodation and meals are billed directly by the center at €190 per day.</p>
              <div className="bg-white rounded-lg border border-[#a9e5c9] overflow-hidden shadow-sm">
                <div className="bg-[#264256] px-6 py-4">
                  <h3 className="font-default font-bold text-white text-sm uppercase tracking-wider">What you pay separately</h3>
                </div>
                <div className="divide-y divide-[#eefaf4]">
                  {additionalCosts.map((item, i) => <div key={i} className="flex items-center justify-between px-4 py-4 gap-4">
                      <span className="font-default text-[#264256] text-sm leading-snug">{item.item}</span>
                      <span className="font-default font-bold text-[#264256] text-sm shrink-0">{item.price}</span>
                    </div>)}
                </div>
                <div className="px-4 py-4 border-t border-[#eefaf4]">
                  <p className="font-default text-muted-foreground text-xs leading-relaxed">Egle Rehabilitation center rate: €190 per day including private room and 5 meals. Recommended stay: 12 to 20 days (following 1 to 2 nights post-operative care at Clinic Agatas). Total: €2,280 to €3,800. Partial HSE reimbursement may apply for physiotherapy procedures.</p>
                  <a href="/how-it-works/" className="font-default text-[#264256] text-xs font-bold uppercase tracking-wider hover:text-[#65d19d] transition-colors mt-2 block">HSE reimbursement explained</a>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <h2 className="font-default font-bold text-2xl text-[#264256] leading-tight">HSE reimbursement rates</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">HSE reimburses the lesser of what you paid or the Irish public rate for the same procedure. Rates below are from the official HSE day case price list effective 1 July 2025.</p>
              <div className="bg-white rounded-lg border border-[#a9e5c9] overflow-hidden shadow-sm">
                <div className="bg-[#264256] px-6 py-4">
                  <h3 className="font-default font-bold text-white text-sm uppercase tracking-wider">Maximum HSE Reimbursement</h3>
                </div>
                <div className="divide-y divide-[#eefaf4]">
                  {reimbursements.map((item, i) => <div key={i} className="flex items-center justify-between px-4 py-4 gap-4">
                      <span className="font-default text-[#264256] text-sm">{item.procedure}</span>
                      <span className="font-default font-bold text-[#264256] text-base shrink-0">{item.hse}</span>
                    </div>)}
                </div>
                <div className="px-4 py-4 border-t border-[#eefaf4]">
                  <div className="bg-[#264256] rounded-lg p-4">
                    <p className="font-default text-white/80 text-xs leading-relaxed">All procedures at Clinic Agatas are classified as day case under Lithuanian healthcare regulations. HSE reimbursement is calculated at the Irish day case rate.</p>
                    <a href="/how-it-works/" className="font-default text-[#65d19d] text-xs font-bold uppercase tracking-wider hover:underline mt-2 block">HSE reimbursement explained</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function ProceduresAndHseReimbursement() {
  return <>
      <Hero />
      <PricesSection />
      <CoordinationPackageSection />
      <AdditionalCostsSection />
      <section className="bg-background py-10 md:py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-[#264256] rounded-2xl px-6 md:px-10 py-10 md:py-14 text-center flex flex-col items-center gap-6 md:gap-8">
            <div className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-white leading-tight">All costs confirmed before you travel.</h2>
              <p className="font-default text-white/80 text-sm md:text-base leading-relaxed max-w-xl mx-auto">We provide a full written cost breakdown after your online consultation. No surprises on arrival.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
                <Link to="/#request-your-consultation">Book a Consultation</Link>
              </Button>
              <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
                <a href="/how-it-works/">HSE Reimbursement Info</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>;
}